import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../lib/api';

// Convert base64 URL to Uint8Array for applicationServerKey
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
}

export default function usePushNotifications() {
  const [permission, setPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  );
  const [subscription, setSubscription] = useState(null);
  const [isSupported, setIsSupported] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Check browser support
  useEffect(() => {
    const supported = 
      typeof window !== 'undefined' &&
      'serviceWorker' in navigator && 
      'PushManager' in window &&
      'Notification' in window;
    setIsSupported(supported);
  }, []);

  // Register service worker
  const registerServiceWorker = useCallback(async () => {
    try {
      const registration = await navigator.serviceWorker.register('/service-worker.js', {
        scope: '/'
      });
      console.log('Service Worker registered:', registration.scope);
      return registration;
    } catch (err) {
      console.error('Service Worker registration failed:', err);
      throw err;
    }
  }, []);

  // Check for existing subscription
  const checkExistingSubscription = useCallback(async () => {
    try {
      if (!('serviceWorker' in navigator)) return null;

      const registration = await navigator.serviceWorker.ready;
      const existingSubscription = await registration.pushManager.getSubscription();

      if (existingSubscription) {
        setSubscription(existingSubscription);
      }

      return existingSubscription;
    } catch (err) {
      console.error('Error checking subscription:', err);
      return null;
    }
  }, []);

  // Request permission and subscribe
  const subscribe = useCallback(async () => {
    if (!isSupported) {
      setError('Push notifications not supported');
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Request notification permission
      const permissionResult = await Notification.requestPermission();
      setPermission(permissionResult);

      if (permissionResult !== 'granted') {
        throw new Error('Notification permission denied');
      }

      // Register service worker
      const registration = await registerServiceWorker();
      await navigator.serviceWorker.ready;

      // Fetch VAPID public key from server
      const keyResponse = await api.get(`/push/vapid-public-key`);
      
      const { public_key } = keyResponse.data;
      if (!public_key) {
        throw new Error('Push notifications not configured on server');
      }

      // Subscribe to push
      const pushSubscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(public_key)
      });

      // Convert keys to base64 for server
      const p256dhKey = pushSubscription.getKey('p256dh');
      const authKey = pushSubscription.getKey('auth');
      
      const subscriptionData = {
        subscription: {
          endpoint: pushSubscription.endpoint,
          keys: {
            p256dh: btoa(String.fromCharCode(...new Uint8Array(p256dhKey))),
            auth: btoa(String.fromCharCode(...new Uint8Array(authKey)))
          }
        },
        user_agent: navigator.userAgent
      };

      // Save subscription to server
      await api.post(`/push/subscribe`, subscriptionData);

      setSubscription(pushSubscription);
      return pushSubscription;
    } catch (err) {
      console.error('Subscription error:', err);
      setError(err.response?.data?.detail || err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [isSupported, registerServiceWorker]);

  // Unsubscribe
  const unsubscribe = useCallback(async () => {
    try {
      if (!subscription) {
        throw new Error('No active subscription');
      }

      // Remove from server
      await api.post(`/push/unsubscribe`, null, {
        params: { endpoint: subscription.endpoint }
      });

      // Unsubscribe locally
      await subscription.unsubscribe();
      setSubscription(null);
    } catch (err) {
      console.error('Unsubscribe error:', err);
      setError(err.message);
      throw err;
    }
  }, [subscription]);

  // Initialize on mount
  useEffect(() => {
    if (isSupported) {
      checkExistingSubscription();
    }
  }, [isSupported, checkExistingSubscription]);

  return {
    permission,
    subscription,
    isSupported,
    isLoading,
    error,
    subscribe,
    unsubscribe,
    checkExistingSubscription
  };
}
